import styled, { css } from 'styled-components';
import { FindingsSeverity } from '../../utils/services';

interface ResultContentProps {
  mode: string;
}

export const ProfileContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;

  img {
    width: 300px;
    height: 200px;
    object-fit: contain;
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
  }

  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;

    img {
      width: 100%;
      height: 200px;
      border: 0;
      border-radius: var(--radius-md);
    }
  }
`;

export const ResultContent = styled.div<ResultContentProps>`
  display: flex;
  width: 100%;
  align-items: center;
  margin-top: 0;
  border-radius: var(--radius-md);

  /* Tons escuros: esta faixa leva o texto do resultado em branco, e os tons
     vivos não dão contraste (branco sobre o amarelo ficava ilegível). */
  ${props =>
    props.mode === 'success' &&
    css`
      background: var(--feedback-success-strong);
    `};
  ${props =>
    props.mode === 'error' &&
    css`
      background: var(--feedback-danger-strong);
    `};
  ${props =>
    props.mode === 'warn' &&
    css`
      background: var(--feedback-warning-strong);
    `};
`;

/* Texto de dentro do ResultContent — um componente próprio em vez de um
   seletor `p` genérico dentro de ResultContent. Motivo: ResultContent é
   renderizado dentro de ProfileData, que também estiliza `p` (as legendas
   "Nome"/"Documento", em --muted-foreground); as duas regras têm a mesma
   especificidade e a ordem de inserção do styled-components decidia o
   empate — na prática o texto do resultado saía cinza em vez de branco. */
export const ResultStatus = styled.p`
  /* O "&&" dobra a classe gerada (.hash.hash), subindo a especificidade pra
     (0,2,0) — o seletor "p" do ProfileData, por vir de uma classe + a tag
     (0,1,1), tem especificidade MAIOR que uma classe só (0,1,0); trocar de
     seletor de tag pra um componente próprio, sozinho, não bastava pra
     vencer esse empate (foi por isso que o branco ainda não pegava). */
  && {
    display: flex;
    align-items: center;
    font-size: 1.4rem;
    font-weight: 700;
    padding: 1rem;
    border-radius: var(--radius-md);
    color: #fff;

    svg {
      margin-right: 1rem;
    }
  }
`;

export const ProfileData = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-left: 2rem;

  @media (max-width: 600px) {
    padding-left: 0;
    margin-bottom: 2rem;
    align-items: center;
    justify-content: center;
  }

  p {
    margin-bottom: 0.25rem;
    color: var(--muted-foreground);
    font-size: 0.8rem;
  }

  span {
    font-weight: 700;
    margin-bottom: 1rem;
  }

  div:last-child {
    margin-top: 0;

    @media (max-width: 600px) {
      place-content: center;
    }
  }
`;

export const MessageError = styled.span`
  width: 100%;
  text-align: center;
  padding: 1rem 0;
  font-weight: 600;
`;

/* Fica alinhado à direita, dentro do cabeçalho do card (56px de altura). */
export const BadgeResult = styled.span`
  position: absolute;
  z-index: 10;
  top: 13px;
  right: 1.5rem;
  border-radius: var(--radius-full);
  /* Também leva texto branco — mesmo motivo do ResultContent. */
  background: var(--feedback-danger-strong);
  padding: 0.35rem 0.8rem;
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #fff;

  @media (max-width: 600px) {
    right: 1rem;
  }
`;

/* Sem limite de altura próprio: cresce com o card e deixa a página rolar
   naturalmente — os cards de processo (título + descrição + vários campos)
   ficam altos demais pra caber numa caixinha de scroll interna fixa. */
export const FindingsList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`;

/* Faixa consolidada acima da lista: o total de apontamentos DA PESSOA e em
   quais serviços eles caíram. Cada accordion abaixo já traz o seu próprio
   serviço e a sua própria contagem, mas ler o todo exigia somar de cabeça —
   esta linha responde "quanto e onde" de uma vez. */
export const FindingsOverview = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid #f3c9c5;
  border-left: 4px solid var(--feedback-danger-strong);
  border-radius: var(--radius-md);
  background: #fdecea;

  > span {
    font-size: 0.82rem;
    color: #8a1f18;
  }
`;

/* Nome de um serviço que apontou, dentro da faixa consolidada. */
export const ServiceChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: var(--radius-full);
  border: 1px solid #f3c9c5;
  background: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--feedback-danger-strong);
`;

/* Um card por processo/registro dentro do accordion de cada serviço — a
   Brick pode devolver mais de um (ex.: 2 processos criminais no mesmo CPF). */
/* Agrupa o contador de apontamentos e o selo de status no cabeçalho do
   accordion — o Summary do accordion é space-between com dois filhos, então
   os dois precisam vir juntos do lado direito. */
export const SummaryTags = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  /* Os selos não encolhem; quem cede espaço é o nome do serviço. */
  flex: 0 0 auto;
`;

/* Nome do serviço no cabeçalho do accordion. Agora é o nome completo (e não
   mais o código de 3 letras), então precisa poder quebrar em telas estreitas
   em vez de empurrar os selos para fora do card. */
export const ServiceLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
`;

/* Quantidade de apontamentos do serviço; fundo colorido pela faixa de
   severidade, na mesma rampa usada no resumo do atendimento. */
const severityBackground: Record<FindingsSeverity, string> = {
  none: 'var(--stone-400)',
  low: '#ca8a04',
  medium: '#ea580c',
  high: '#dc2626',
};

export const FindingsCount = styled.span<{ severity: FindingsSeverity }>`
  min-width: 1.35rem;
  padding: 0.15rem 0.4rem;
  border-radius: var(--radius-full);
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  color: #fff;
  background: ${(props: { severity: FindingsSeverity }) =>
    severityBackground[props.severity]};
`;

export const ProcessList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const ProcessCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.5);

  & + & {
    margin-top: 0;
  }

  strong {
    font-size: 0.85rem;
  }

  p {
    margin: 0;
    font-size: 0.8rem;
    color: var(--muted-foreground);
    line-height: 1.4;
  }
`;

export const ProcessField = styled.div`
  display: flex;
  gap: 0.35rem;
  font-size: 0.78rem;

  dt {
    flex: 0 0 auto;
    font-weight: 600;
    color: var(--muted-foreground);
  }

  dd {
    margin: 0;
  }
`;
